/* eslint-disable @typescript-eslint/no-explicit-any */
import { DOCUMENT } from '@angular/common';
import {
  Component,
  forwardRef,
  AfterViewInit,
  OnDestroy,
  Input,
  ElementRef,
  Renderer2,
  ChangeDetectorRef,
  NgZone,
  Inject,
  ViewChild,
  AfterContentChecked
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { OverlayService, PrimeNGConfig } from 'primeng/api';
import { Calendar } from 'primeng/calendar';

@Component({
  selector: 'm-calendar',
  templateUrl: './m-calendar.component.html',
  styleUrls: ['./m-calendar.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MCalendarComponent),
      multi: true
    }
  ]
})
export class MCalendarComponent
  extends Calendar
  implements
    ControlValueAccessor,
    AfterViewInit,
    OnDestroy,
    AfterContentChecked
{
  @ViewChild(Calendar) pCalendar!: Calendar;
  @Input() resolution: string | null = null;

  private hoverUnlistener: (() => void) | null = null;
  private selectedStart: HTMLElement | null = null;
  private selectedEnd: HTMLElement | null = null;

  // CVA callbacks - store the callbacks Angular gives us
  private onChangeCb: (value: any) => void = () => {
    // Default empty callback
  };

  private onTouchedCb: () => void = () => {
    // Default empty callback
  };

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.attachHoverListeners();
    if (this.pCalendar && !this.inline && this.pCalendar.inputfieldViewChild) {
      this.renderer.listen(
        this.pCalendar.inputfieldViewChild.nativeElement,
        'click',
        () => {
          this.pCalendar.toggle();
          this.pCalendar.cd.markForCheck();
        }
      );
    }
    requestAnimationFrame(() => {
      this.applyStartAndEndClasses(this.value);
    });
  }

  override ngOnDestroy() {
    this.detachHoverListeners();
    super.ngOnDestroy();
  }

  constructor(
    @Inject(DOCUMENT) document: Document,
    el: ElementRef,
    renderer: Renderer2,
    cd: ChangeDetectorRef,
    zone: NgZone,
    config: PrimeNGConfig,
    overlayService: OverlayService
  ) {
    super(document, el, renderer, cd, zone, config, overlayService);
  }

  // ControlValueAccessor implementation
  override writeValue(value: any): void {
    this.value = value;
    this.cd.markForCheck();
  }

  override registerOnChange(fn: (value: any) => void): void {
    this.onChangeCb = fn;
  }

  override registerOnTouched(fn: () => void): void {
    this.onTouchedCb = fn;
  }

  override setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  ngAfterContentChecked(): void {
    this.applyStartAndEndClasses(this.value);
  }

  // Handle inner p-calendar model changes and propagate to parent
  onInnerModelChange = (val: any) => {
    this.value = val; // keep the inherited state in sync
    this.onChangeCb(val); // propagate up to the wrapper's consumer
    this.onTouchedCb();
  };

  attachHoverListeners(): void {
    this.hoverUnlistener = this.renderer.listen(
      this.el.nativeElement,
      'mouseover',
      this.onHoverDate.bind(this)
    );
  }

  detachHoverListeners(): void {
    if (this.hoverUnlistener) {
      this.hoverUnlistener();
      this.hoverUnlistener = null;
    }
  }

  onHoverDate(event: MouseEvent): void {
    if (this.selectionMode === 'range' && this.resolution === 'week') {
      this.clearPreviousPreviewHighlight();
      const target = (event.target as HTMLElement).closest('tr');
      if (target) {
        this.renderer.addClass(target, 'preview-range');
      }
    }
  }

  clearPreviousPreviewHighlight(): void {
    const highlightedElements =
      this.el.nativeElement.querySelectorAll('.preview-range');
    highlightedElements.forEach((el: HTMLElement) => {
      this.renderer.removeClass(el, 'preview-range');
    });
  }

  applyStartAndEndClasses(dates: Date[]): void {
    if (!Array.isArray(dates)) return;

    const [startDate, endDate] = dates;

    this.clearSelections();
    if (startDate) {
      const startKey = this.formatDateKey(startDate);
      const startElement = this.el.nativeElement
        .querySelector(`[data-date="${startKey}"]:not(.p-disabled)`)
        ?.closest('td');
      if (startElement) {
        startElement.classList.add('selected-start');
      }
    }
    if (endDate) {
      const endKey = this.formatDateKey(endDate);
      const endElement = this.el.nativeElement
        .querySelector(`[data-date="${endKey}"]:not(.p-disabled)`)
        ?.closest('td');
      if (endElement) {
        endElement.classList.add('selected-end');
      }
    }
  }

  clearSelections(): void {
    // Clear 'selected-start' and 'selected-end' classes
    const startElement = this.el.nativeElement.querySelector('.selected-start');
    const endElement = this.el.nativeElement.querySelector('.selected-end');

    if (startElement) {
      this.renderer.removeClass(startElement, 'selected-start');
    }
    if (endElement) {
      this.renderer.removeClass(endElement, 'selected-end');
    }
  }

  handleShow(event: any) {
    if (this.selectionMode === 'range' && this.resolution !== 'week') {
      setTimeout(() => {
        this.applyStartAndEndClasses(this.value);
      }, 100);
    }
    this.onShow.emit(event);
  }

  handleMonthChange(event: any) {
    if (this.selectionMode === 'range' && this.resolution !== 'week') {
      setTimeout(() => {
        this.applyStartAndEndClasses(this.value);
      }, 100);
    }
    this.onMonthChange.emit(event);
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.selectionMode === 'range') {
      this.handleSelectionRange(event);
    }
  }

  handleSelectionRange(event: KeyboardEvent): void {
    const value = (event.target as HTMLInputElement).value;
    const values = value.split(' - ');
    if (values.length === 2) {
      const startDate = new Date(values[0] as string);
      const endDate = new Date(values[1] as string);
      if (
        this.isValid(startDate) &&
        this.isValid(endDate) &&
        this.validateRange(startDate, endDate)
      ) {
        this.value = [startDate, endDate];
        this.onSelect.emit(startDate);
        this.onSelect.emit(endDate);
      }
    }
  }

  isValid(date: Date): boolean {
    return !isNaN(date.getTime());
  }

  validateRange(startDate: Date, endDate: Date): boolean {
    if (this.minDate && startDate < this.minDate) {
      return false;
    }
    if (this.maxDate && endDate > this.maxDate) {
      return false;
    }
    if (startDate > endDate) {
      return false;
    }
    return true;
  }
}
